/**
 * This file contains all Entities and DTOs as "schema" used by the client and shared with the backend/API.
 * It also contains used enumerations (enums), and standard "fresh"-objects based on the schemas,
 * as well as examples for testing.
 */

import { StringBasedStringArray } from "@basics/common"
import { ExtendsWithAllPropertiesRequired } from "@basics/typescript-util"
import { EntityType } from "@redux/common/reduxTypes"
import { ILockableModel } from "@redux/usecases/lock/schema-definitions"

import { IChallenge, IFund, IFundApplication, IProposal } from "./schema/challenges"
import { AvailableContentItems } from "./schema/content-items"

/* ************************************************************************** */
/* Interfaces: used schema for all entities and DTOs                          */
/* using the prefix I to have a standard in the code                          */
/* ************************************************************************** */

// #region basic technical schemas

/* ********** Technical schemas ********************************************* */

/**
 * IRI stub following the pattern /endpointurl
 * NOTE: the stub may possibly include more slashes, e.g. /end/point/url
 * but also invalid/unwanted combinations like e.g. /end///point
 */
export type IRIstub = `/${string}`

/**
 * pattern of an url of a sub resource under a base endpoint, e.g. /projects/7/memberships
 */
export type SubResourceUrlPattern = `/${string}/[id]/${string}`

/**
 * real sub resource url after the [id] was replaced by a real id
 */
export type SubResourceUrl = `/${string}/${number}/${string}`


/**
 * to mark IRI elements explitely they are typed as IRI, which is a string
 * of special pattern
 *
 * @see: https://stackoverflow.com/questions/51445767/how-to-define-a-regex-matched-string-type-in-typescript
 */
export type IRI = `${IRIstub}/${number}`

/**
 * That is the return type of those endpoints that do not return any data output
 * (entities, entity lists, downloads, ...),
 * e.g.:
 * - verification of account, email, password
 * - M2M endpoints
 * - delete endpoints, e.g. attachment definition
 * - lock/undlock/contact/contact users endpoints, e.g. for idea and program entity
 * ... and many more
 *
 * This type is called OperationResultOutput in the backend.
 */
export interface IOperationResultOutput {
  /**
   * Result string, e.g. "Verification successful"
   */
  result: string
  /**
   * Contains any data that may be relevant to the caller.
   * Especially: for account verification it contains the temporarily stored onboarding data
   * when the client provided such data when registering a user.
   */
  metadata: any[]
}

/**
 * An INativeHydraCollection is used for the transport of a collection of objects from the backend-API.
 */
export interface INativeHydraCollection<T> {
  /**
   * the context of the EntityType in the backend,
   * e.g. "/contexts/Project"
   */
  "@context"?: string
  /**
   * endpoint url of the entity type of the fetched entities
   * e.g. "/projects"
   */
  "@id"?: IRIstub
  /**
   * usually contains: "Collection"
   */
  "@type"?: string
  /**
   * an array of the fetched entities, filtered by the current used filter
   */
  "member"?: T[]
  /**
   * is a complex object that describes, what search/filter options are provided by the endpoint
   *
   * @todo https://futureprojects.atlassian.net/browse/FCP-1446
   */
  "search"?: object
  /**
   * Number of items that are available by the current used filter: is higher than the number of members when more
   * than one page is available.
   */
  "totalItems"?: number
  /**
   * Pagination meta data
   *
   * @see https://api-platform.com/docs/core/pagination/
   */
  "view"?: {
    /** url of the current page */
    "@id"?: string
    /** usually: "PartialCollectionView" */
    "@type"?: string
    /** url of the first page when the collection is parted into more than one page */
    "first"?: string
    /** url of the last page when the collection is parted into more than one page */
    "last"?: string
    /** url of the next page from the point of view of the current api collection call */
    "next"?: string
    /** url of the previous page from the point of view of the current api collection call */
    "previous"?: string
  }
}

/**
 * An IHydraCollection is used for the transport of a collection of entities based on IModel from the backend-API.
 */
export type IHydraCollection<T extends IModel> = INativeHydraCollection<T>

/**
 * structure of an error returned by the backend API
 */
export interface IHydraError {
  "@context": string
  "@type": string
  "title": string
  /** @deprecated */
  "description": string
  "detail": string
}



/**
 * An IModel is the basic model of all used Entities.
 */
export interface IModel {
  /**
   * optional, to be undefined when creating a new entity: the IRI does not exist yet, the backend will create it
   */
  "@id"?: IRI
  /**
   * usedPrivileges are those privileges with which the users request was handled by the backend.
   * A user may have more than one privilege, but the request was performed as one of them, e.g. PlatformManager.
   */
  usedPrivileges?: UserPrivileges[]
  /** true, if all data of the Object was retrieved; false, if the object was loaded as part of a collection only */
  detailResult?: boolean

  /**
   * @deprecated @see https://futureprojects.atlassian.net/browse/FCP-1627
   * // TODO: property is not available in multitenant
   * // @todo multi refactor: replace by using usedPrivileges
   */
  usedRoles?: UserRole[]
}

/**
 * An INumericIdentifierModel extends the IModel by a numeric identifier,
 * so all unique entities can by identified by that id.
 */
export interface INumericIdentifierModel extends IModel {
  id?: number
}

/**
 * An ITransitionableModel extends the INumericIdentifierModel by a transition,
 * so all entities of this type may be transitioned.
 * This refers to backend types that are modeled to have transitions, i.e. they have:
 * an entry in workflow.yaml; an AbstractTransitionInput subclass; an `/transition` endpoint.
 *
 * @template TransitionEnum list of transitions that may be performed
 */
export interface ITransitionableModel<TransitionEnum> extends INumericIdentifierModel {
  transitions?: ITransitionList<TransitionEnum>
}

/**
 * An ISlugIdentifierModel extends the INumericIdentifierModel by a string identifier,
 * so all unique entities can be identified by that slug.
 */
export interface ISlugAndNumericIdentifierModel extends INumericIdentifierModel {
  slug?: string
}

/**
 * Entity types that are reportable for misuse.
 *
 * NOTE: Using a type instead of defining a common super interface, b/c how TypeScript works:
 * Reportable entity definitions do not have a specific attribute that could help TypeScript
 * distinguish them from other entity definitions.
 * TODO: However, it could be relevant to check if this may be replaced by an enum, like e.g. LoadableEntityType
 */
export type ReportableModel = IProject | IIdea | IProvider | IProgram | IUser | IFund | IChallenge

// #endregion

/* ********** Schemas for user and login handling ********************************************* */

/**
 * An IAuthReply represents a token for an authentificated connection between client and backend and can be used
 * to order a new token.
 */
export interface IAuthReply {
  /** encoded JWT */
  token: string
  /** token string to request a new JWT */
  refresh_token: string
  /** unix timestamp when the refresh-token expires */
  refresh_token_expires: number
}



export interface IEmailChange {
  confirmationPassword: string
  email: string
  verificationUrl?: string
}

export interface INewPasswordRequest {
  verificationUrl?: string
}

export interface IPasswordChange {
  confirmationPassword: string
  password?: string
}

export interface IPasswordReset extends IVerification {
  password: string
}

export interface IPasswordResetRequest {
  username: string
  verificationUrl?: string
}

export interface IVerification {
  id: string
  token: string
}

// is used to send an email to certain users and the sender will get a confirmation email
// @see src/redux/saga/users.ts: sendMenssageToAllUsersSaga
export interface IUserEmail extends ITeamEmail {
  receiverOption: UserEmailReceiverOption
}

/* ********** Content schemas ********************************************* */

/** all characters in uppercase to be used as typisation of ISO3116 */
type UpperCaseCharacter = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X' | 'Y' | 'Z'
/** 2-character string to be used as ISO 3166-1 alpha-2: country code */
export type ISO3116_1_ALPHA_2 = `${UpperCaseCharacter}${UpperCaseCharacter}`

/**
 * An IAddress represents an address of an institution or person.
 */
export interface IAddress {
  "@type": string
  addressInfo?: string
  city?: string
  contactPerson?: string
  /** ISO 3166-1 alpha-2, types here as: a string with 2 characters */
  country?: ISO3116_1_ALPHA_2
  email?: string
  geoJson?: string[]
  name: string
  phone?: string
  postalCode?: string
  street?: string
  url?: string
}

/**
 * An ICategory is used to organize the categories of a project.
 * If a Category does not have properties, their display name should be created using their ID and translated via I18N.
 */
export interface ICategory extends INumericIdentifierModel, IUpdatedAtAndBy {
  createdAt?: string
  createdBy?: IUser
  /** even longer description of the category */
  description?: string
  /** list of problems, related to a category */
  problems?: string[]
  program?: IProgram
  /** a longer describing subtitle */
  subtitle?: string
  /** short title of the category, e.g. "Kunst & Kultur"  */
  title: string
}


/**
 * An ICashFlowEntry represents a single flow of cash.
 * It consists of a number of costs (count) at a certain point in time, with an optional note.
 */
export interface ICashFlowEntry {
  "@type": string
  timePoint: number
  unitCost: number
  quantity: number
  description?: string
}

/**
 * An ICredentials represents the login-data for a single user.
 */
export interface ICredentials {
  username: string
  password: string
}

/**
 * An IDiscussion represents an discussion, consisting of IFeedbackPost, possibly regarding to an IFeedbackInvitation
 * regarding a relatedObject
 */
export interface IDiscussion extends INumericIdentifierModel {
  /** when commenting a project (as relatedObject) a special aspect of that project may be commented. Which one is encoded in the contendId */
  contentId: string | AvailableContentItems
  /** date-time, when the discussion was created */
  createdAt?: Date | string // type: date-time
  /** user qho created tha discussion, may be null */
  createdBy?: IUser
  /** if the discussion was an answer to an FeedbackInvitation */
  feedbackInvitation?: IFeedbackInvitation | IRI
  /** date-time of the youngest post */
  lastPostDate?: Date | string
  /** number of posts to a discussion */
  postCount?: number
  /** the initial post is that one, that founded the discussion */
  initialPost: IFeedbackPost
  /** a discussion may relate to an object of type IProject, IProgram or IUser. Either relatedObject OR feedbackInvitation may be set */
  relatedObject?: IRI
}



/**
 * An IFeedbackInvitation represents an invitation for feedback
 */
export interface IFeedbackInvitation extends INumericIdentifierModel {
  /**
   * type: date-time | null
   */
  activeUntil?: Date | string | null
  anonymousFeedback?: boolean
  /**
   * type: date-time
   */
  createdAt?: Date | string
  createdBy?: IUser
  description?: string
  discussionCount?: number
  questions: IFeedbackQuestion[]
  /**
   * Receiver of an invitation may be the "user group" of an IProgram, IProject or a single IUser,
   * so the IRI of this entity is stored here.
   * Those users are allowed to answer to this FeedbackInvitation
   *
   * Or its "all users" (encoded by empty string), so no IRI is restricting the receiver group.
   */
  receiver?: IRI | ''
  relatedObject?: IRI
  title: string
  type?: string
}

/**
 * type of a single content element of an IFeedbackPost
 */
export type FeedbackPostContent = StringBasedStringArray

/**
 * An IFeedbackPost represents a single post, connected to an IDiscussion answering to an IFeedbackInvitation
 */
export interface IFeedbackPost extends INumericIdentifierModel {
  /** single content-elements, 1 item for comments, more than 1 item for question-lists of FeedbackInvitations */
  content?: FeedbackPostContent
  /** date-time, when the post has been created */
  createdAt?: Date | string
  /** user or null (when anonymous) */
  createdBy?: IUser
  /** related discussion of the post */
  discussion?: IDiscussion | IRI
  /** optional pre-post this FeedbackPost relates to; IRI-Reference */
  parent?: IRI
  /** type of the post */
  type?: FeedbackPostType
}

/**
 * A IFeedbackQuestion represents a single question for an IFeedbackInvitation
 */
export interface IFeedbackQuestion {
  id: string
  explanation?: string
  question: string
  version: number
}

/**
 * Base interface for impact and goal.
 */
export interface IGoalAndMeasurability {
  /** Type of the IGoalAndMeasurability format. */
  "@type": string
  /** goal to be reached */
  goal: string
  /** how to measure that the goal is reached */
  measurability: string
}

/**
 * Each goal consists of the a description itself as well as a text for measurability of Achievement of the goal.
 */
export interface IGoal extends IGoalAndMeasurability {
  /** Percentage of realisation. */
  selfAssessment: number
}

/**
 * Extends the goal by a relevanceEstimation.
 */
export interface IImpact extends IGoalAndMeasurability {
  /** Intensity of the impact from 0-100. */
  relevanceEstimation: number
}

/**
 * An IMediaObject represents a media object, e.g. an image. It is used to handle all kinds of files.
 */
export interface IMediaObject {
  contentUrl: string
  mimeType: string
  name: string
  originalName: string
  size: number
  dimensions?: [number, number]
  createdAt?: Date | string
  createdBy?: IUser
}

/**
 * An IProgram represents a participation program, organized by one tenant to generate ideas and projects.
 */
export interface IProgram extends ISlugAndNumericIdentifierModel, ILockableModel, IUpdatedAtAndBy {
  "@type"?: "Program"
  // TODO make iri type safe for all interfaces => https://futureprojects.atlassian.net/browse/FCP-2046
  "@id"?: `/programs/${number}`
  autoActivation?: boolean
  /**
   * Defines, if the communitymanagers of a program are allowed to access
   * the detailed data of the connected projects.
   * Changes the access permissions for project single calls at the backend API.
   *
   * Is only settable when the program is not activated yet to make the extended visibility
   * transparent to the project users.
   */
  canAccessProjectInternals?: boolean
  color?: string
  createdAt?: string
  createdBy?: IUser
  description?: string
  endDate?: Date | string
  geoBoundingBox?: [[number, number], [number, number]]
  geoPosition?: [number, number]
  locale?: string
  logo?: IMediaObject
  mailSenderName?: string
  name?: string
  /**
   * Number of projects associated to this program.
   * Is readable in program collections to be shown in a "market of programs".
   *
   * NOTE: is updated every time an API request for project creation/deletion is processed.
   * WARN: may differ from real project numbers, if projects are deleted or inserted directly
   * into the database and not via the API.
   */
  projectCount?: number
  provider?: IProvider | IRI
  region?: string
  state?: ProgramState
  targetGroup?: string
  timezone?: string
  vision?: string

  // the following properties belonging to the old IProcess
  // and there are only added here to enable a step by step migration of the schema, pages, components and tests.
  // @todo migrate https://futureprojects.atlassian.net/browse/FCP-1428
  /** @deprecated */
  maxTeamUploadSize?: number
  /** @deprecated */
  maxTeamUploadTotalSize?: number
  // @todo https://futureprojects.atlassian.net/browse/FCP-1431
  /** @deprecated */
  criteria?: string[]
  /** @deprecated */
  goals?: string[]
  /** @deprecated */
  challenges?: IChallenge[]
  /** @deprecated */
  maxPublicDaysForSupportRequest?: number
}

export interface IBankDetails {
  "@type": string
  accountOwner?: string
  bankName?: string
  bic?: string
  iban?: string
}

/**
 * This is an super interface of IProject and IProposalSubmissionData.
 */
export interface IPartialProjectData<
  CategoryType extends IRI[] | string[] = IRI[] | string[],
  ImplementationBeginType extends Date | string = Date | string
> {
  bankDetails?: IBankDetails
  /**
   * Can be of type IRI[] or string[], because
   * the sub-interface IProject uses IRI, but IProposalSubmission uses string because it’s more “final”.
   */
  categories?: CategoryType
  challenges?: string
  contact?: IAddress
  expectedInvolvedPersons?: number
  delimitation?: string
  description?: string
  descriptionExtension?: string
  geoJson?: GeoJSON.Feature | GeoJSON.FeatureCollection
  goalExplanation?: string
  goals?: IGoal[]
  holder?: IAddress
  impact?: IImpact[]
  /**
   * Can be of type Date or string, because
   * the sub-interface IProject uses Date or string, but IProposalSubmission uses only string because it’s more “final”.
   */
  implementationBegin?: ImplementationBeginType
  implementationLocations?: string[]
  implementationState?: ImplementationState
  implementationTime?: number
  name?: string
  partners?: IProjectPartner[]
  outcome?: string[]
  results?: string[]
  resourceRequirements?: IResourceRequirement[]
  sdgExplanation?: string
  sdgs?: SDG[]
  shortDescription?: string
  targetGroups?: string[]
  tasks?: IProjectTask[]
  utilization?: string
  vision?: string
  workPackages?: IWorkPackage[]
}

/**
 * An IProject represents a project. Projects are central entities in the system. Most activities of the user aim
 * at improving project plans.
 *
 * @todo multi categories: update the project import @see https://futureprojects.atlassian.net/browse/FCP-1683
 */
export interface IProject extends ISlugAndNumericIdentifierModel, IPartialProjectData<IRI[], Date | string>, ILockableModel, IUpdatedAtAndBy {
  "@type"?: "Project"
  /**
   * Is this project accepted in the current program?
   *
   * Relevant for programs that make a check on (new) projects.
   */
  accepted?: boolean
  createdAt?: Date | string
  createdBy?: IUser
  /** ID of the entity at the external provider (if it comes from an external provider, e.g. SINN Sachsen); probably a number. */
  externalIdentifier?: string
  /** Short name of the external provider (if the entity comes from an external provider, e.g. SINN Sachsen). */
  externalProvider?: string
  feedbackAllowedFor?: FeedbackRoles[]
  followerCount?: number
  /**
   * @deprecated
   */
  followerships?: IProjectFollowership[]
  inspirationIdea?: IIdea
  inspirationProject?: IProject
  /** Includes only coordinators, planners. No Observers. */
  memberCount?: number
  name?: string
  nextRatePlan?: null // @todo multi add IProjectRatePlan
  partners?: IProjectPartner[]
  pdfFile?: IMediaObject
  picture?: IMediaObject
  pictureUrl?: string
  planSelfAssessment?: SelfAssessment
  profileSelfAssessment?: SelfAssessment
  program?: IProgram | IRI
  programClassificationCode?: number
  /** @deprecated use implementationState */
  progress?: ProjectProgress
  /**
   * @deprecated
   */
  proposals?: (IProposal | IFundApplication)[]
  ratePlan?: null // @todo mult add IProjectRatePlan
  ratePlanChangeDate?: Date | string // @todo multi just date or only string?
  ratePlanMetadata?: any[]
  resultingProjectCount?: number
  /** @deprecated fetch via SubResource => currently not implemented in Backend */
  resultingProjects?: IProject[]
  state?: ProjectState
  teamContact?: IUser
  visibility?: ProjectVisibility
  visualization?: IMediaObject
}

/**
 * Represents an idea that can be picked up any user to develop a project working on that idea.
 * An idea belongs to a program.
 */
export interface IIdea extends INumericIdentifierModel, ILockableModel, IUpdatedAtAndBy {
  categories?: IRI[]
  createdAt?: string
  createdBy?: IUser
  /** core content of an idea */
  description?: string
  /** program the idea belongs to */
  program?: IProgram | IRI
  /** number of projects resulting from this idea */
  resultingProjectCount?: number
  /** @deprecated: wird demnächst über einen eigenen Subressource-Endpunkt für die Ideen abgewickelt */
  resultingProjects?: IProject[]
}

/**
 * interface to centralize motivation and skills for several entity types
 */
export interface IMotivationAndSkills {
  motivation?: string
  skills?: string
}

/**
 * An abstract interface that represents all relationships between project and user.
 * It is only used on client side and allows easier handling of equivalent client-side data structures
 * (i.e. IProjectFollowership).
 */
export interface AbstractProjectUserRelation extends IModel {
  project?: IProject | IRI // @todo: check: this is always an IProject @currentUser, never an IRI, or? Or is it converted to an IRI on writing data? @see https://futureprojects.atlassian.net/browse/FCP-1381
  user?: IUser | IRI
}

/**
 * An IProjectFollowership represents a followership connection between a user and a project.
 * This specific interface models project followerships that contains a project object (instead of IRI),
 * as it is sent from the API in get-user-self usecase.
 */
export interface IProjectFollowership extends AbstractProjectUserRelation, INumericIdentifierModel {
  createdAt?: Date | string
  /**
   * We specialize to IProject and omit string, b/c it's guaranteed that all received data (from API) will contain the object.
   * NOTE since the API requires strings on CREATE actions, we apply doc/principles/data-schema.md "Receiving objects, sending IRIs".
   * In this case, use `transformEntityToWriteDTO` from schema-dto.ts to get a `IProjectFollowershipDTO`.
   */
  project: IProject
}


/**
 * An IProjectImport represents a temporary object for a JSON-Upload and an optional new name for the new project
 */
export interface IProjectImport extends IMotivationAndSkills {
  file?: File
  newName?: string
  // @todo check in the future: are motivation and skills still required in the backend?
  motivation: string
  skills: string
  program: IRI
}


/**
 * An IPartner represents an project partner and its contribution to the project.
 */
export interface IProjectPartner {
  "@type"?: string
  address?: IAddress
  contribution?: string
  domain?: string
  /**
   * uuid, @see addPartner
   * no IRI because it is not a seperate Entity
   */
  id?: string
  state?: PartnershipState
}

/**
 * An IProjectTask represents an task or a job to be done, to implement a project. To plan a project its central
 * to collect all necessary tasks. A task is fulfilled over a flow of time and it can be part of a work package.
 */
export interface IProjectTask {
  "@type": string
  description?: string
  id?: string
  /**
   * in which months of the projects implementationTime this task will be worked on
   * e.g. 1,2,3 for the first 3 months
   */
  months?: number[]
  result?: string
  title?: string
  /**
   * ID of the parent IWorkPackage
   * a uuid, not an IRI or number, @see addWorkPackage()
   */
  workPackage?: string
  order?: number
}

export interface ISupportRequest extends INumericIdentifierModel, IUpdatedAtAndBy {
  content?: string
  /** project, for which a SupportRequest is created */
  project?: IProject
  /** timestamp: date-time, until the SupportRequest is public */
  publicUntil?: Date | string
  state?: SupportRequestState
  supporterRole?: SupporterRole
  title?: string
}

/**
 * An IResourceRequirement represents a resource, that is needed to fulfill a task or a workpackage.
 * It consists of a list of CashFlow-Elements, those sum stands for the needes costs for that ResourceRequirement.
 *
 * A ResourceRequirement needs a source to be fulfilled.
 * A source has a sourceType: funding, ownResources, other.
 * A source can be a fund, own funds, contributions or material or thirdParty/other sources.
 * A source can point on a relatedSourceObject: a Fund-ID or a ProjectPartner-ID
 */
export interface IResourceRequirement {
  "@type": string
  cashFlow?: ICashFlowEntry[]
  /**
   * in which dimension is the cashflow measured?
   */
  cashFlowTimeDimension?: TimeDimension
  /**
   * type of costs, e.g. "personnel"
   */
  costCategory?: ResourceCostCategory
  /**
   * a list of fund-depending subType-Definitions
   */
  costSubCategories?: ICostSubCategory[]
  customUnit?: string
  customSource?: string
  description?: string
  ownContributionsExplanation?: string
  id?: string
  /**
   * valid fund-id (number) or partner-id (string)
   * partner id as uuid, @see addPartner
   */
  relatedSourceObject?: number | string
  /**
   * to precise the source of the resource
   */
  source?: ResourceSource
  /**
   * the type of the source
   */
  sourceType?: ResourceSourceType
  /**
   * ID of the parent IProjectTask (alternate to workPackage | null)
   */
  task?: string
  title: string
  /**
   * physical unit of (e.g. number, hours, km, meals, ...)
   */
  unit: ResourceUnit
  /**
   * for financialType === personnel only
   */
  weeklyHours?: number
  /**
   * ID of the parent IWorkPackage (alternate to task | null)
   */
  workPackage?: string
}

/**
 * An ICostSubCategory represents the name of a subtype of costs, depending on a fund.
 * It allows to adopt the naming of a subtype to the specific needs of a fund.
 */
export interface ICostSubCategory {
  "@type": string
  /**
   * may be an element from the given enum or an custom CostSubCategory from the given fund
   */
  type: CostSubCategory | string
  /**
   * id of the fund
   */
  relatedFund?: number
}

/**
 * Represents in the real world e.g. a company, a socialOrganization, a municipality.
 */
export interface ITenant extends ISlugAndNumericIdentifierModel, ITransitionableModel<TenantTransition>, ILockableModel, IUpdatedAtAndBy {
  "@type"?: string
  address?: IAddress
  invoiceAddress?: IAddress
  color?: string
  createdAt?: Date | string
  createdBy?: IUser
  geoBoundingBox?: [[number, number], [number, number]]
  geoPosition?: [number, number]
  imprint?: string
  /** BCP 47/RFC 4646 language tag @see /locales/locales-config */
  locale?: string
  logo?: IMediaObject
  name?: string
  nature?: TenantNature
  customNature?: string
  /** Information for the platform owners: What is the intention of the tenant on this platform? */
  platformUsage?: string
  ratePlan?: IRatePlan | IRI
  ratePlanChangeDate?: Date | string
  ratePlanMetadata?: string[]
  nextRatePlan?: IRatePlan | IRI
  shortName?: string
  state?: TenantState
  timezone?: string
  vatId?: string
}

/**
 * An IProvider is a special ITenant with possible programs.
 *
 * Hints:
 * If custom program type is part of the programTypes, than the property customProgramType
 * is not allowed to be blank.
 * Require the coreProgram to belong to the provider itself.
 */
export interface IProvider extends ITenant {
  coreProgram?: IProgram
  customProgramType?: string
  programTypes?: ProgramType[]
}

/**
 * ISysinfo is used to transfer information about the configuration of the running system from the backend to the client.
 */
export interface ISysinfo {
  accessBlock: {
    [key: string]: {
      interval: string
      limit: number
    }
  }
  applicationUrl: string
  defaultLocale: string
  defaultTimezone: string
  frontendUrl: string
  jwtTTL: number
  projectInactiveNotificationThreshold: number
  projectInactiveThreshold: number
  userVerificationRequired: boolean
}

/**
 * An ITeamUpload represents a single upload into the file-storage of a member of a project team.
 * It consists of the uploaded file as well as extra information to handle this file.
 *
 * @todo multi Warum ist die property file vom Typ File|IMediaobject und nicht nur vom Typ IMediaobject?
 */
export interface ITeamUpload extends INumericIdentifierModel {
  file?: File | IMediaObject
  category?: string
  content?: string
  project?: IProject | IRI
}



/**
 * An ITeamEmail represents an email, consisting of emaiSubject and emailMessage and a list of emailReceivers.
 */
export interface ITeamEmail {
  message: string
  subject: string
  receivers?: number[]
}

/**
 * An ITransitionList represents a transition in a workflow and the blockers,
 * that prevent the transition to be performed.
 * @template TransitionEnum list of transitions that may be performed by an entity
 */
export type ITransitionList<TransitionEnum> = {
  [transitionName in TransitionEnum as string]: {
    blockers: {
      [code: string]: string
    }
  }
}


/**
 * An IRatePlan is used in the ITenant in order to determine how much the ITenant
 * for how long has to pay with what kind of features.
 *
 * note: the IRatePlan has no detailResult and usedPrivileges
 */
export interface IRatePlan extends IUpdatedAtAndBy {
  id?: number
  "@id"?: IRI
  createdAt?: Date | string
  createdBy?: IUser
  description?: string
  features?: string[]
  isDefault?: boolean
  name?: string
  prices?: number[]
  public?: boolean
  state?: RatePlanState
}



/**
 * An IUser represents a single user: username, name, email, several states and his memberships to projects.
 *
 * @todo multi anpassen des IUser's, u.a. roles durch privileges ersetzen: https://futureprojects.atlassian.net/browse/FCP-1411
 */
export interface IUser extends INumericIdentifierModel, ILockableModel {
  "@type"?: string
  aboutMe?: string
  createdAt?: string
  email?: string
  /**
   * provider name/identifier, in case the user is created by/connected to an external provider, e.g. an OAuth provider
   */
  externalProvider?: string
  /**
   * Username, this user has brought from his external provider.
   * Is undefined/empty, if the user has directly registered on the Projektfabrik and not via an OIDC provider
   */
  externalUsername?: string
  firstName?: string
  lastName?: string
  /** BCP 47/RFC 4646 language tag @see /locales/locales-config */
  locale?: string
  picture?: IMediaObject
  pictureUrl?: string
  password?: string
  roles?: UserRole[]
  timezone?: string
  self?: boolean
  /**
   * For OIDC/M2M created users this is initially set to true.
   * If this flag is true, incoming changes from the provider
   * (login or sync) are reflected by updating the local username. This could
   * mean that (again) special characters are removed / the name is made
   * unique. If this flag is false (can be set by the user), changes are not
   * propagated but the local value is kept. Especially useful when the user
   * chose another (local) username for the Projektfabrik, e.g. when he could
   * not use his external username because of restrictions in length or
   * allowed characters etc.
   */
  useExternalUsername?: boolean
  username?: string
  verified?: boolean

  // the following properties don't exist anymore in multitenant @todo multi delete the properties @see branch-info-single-multi.md
  /** @deprecated */
  active?: boolean
  /**
   * projects that have been created by the user, even if he no longer is a member of the project team
   * includes created ideas
   *
   * @deprecated
   */
  createdProjects?: IProject[]
}

/**
 * Recurring structure of updatedAt/updatedBy
 */
export interface IUpdatedAtAndBy {
  /**
   * Timestamp on which an entity was lately updated.
   */
  updatedAt?: Date | string
  /**
   * User who updated an entity lately.
   */
  updatedBy?: IUser
}

/**
 * A ValidUserObjectRole represents the connection of an user to an object (e.g. a project or an media object)
 * by a special role. It is necessary to organise the role-specific access rights of an user to an object.
 *
 * This abstract interface is extended by entity specific interfaces for entities that may have user-role-connections,
 * e.g. IUserProjectRole etc.
 *
 * This type ensures, that only valid UserObjectRoles are defined.
 * Note: The used numbers within the type definition could be all 1 or 0, but
 * it improves readability, b/c each number sequence is unique and represents one
 * specific UserObjectRole.
 *
 * If the combination of the given generic type parameters are invalid, never will be
 * returned. And to create objects with type never is not possible.
 *
 * Inspired from:
 *
 * @see https://casualdeveloper.net/post/2020-12-21-advanced-typescript-type-tricks/
 */
type ValidUserObjectRole<
  ObjectType extends UserObjectRoleObjectType,
  RoleType extends UserObjectRoleType,
  MetaDataType extends StringBasedStringArray | UserProjectMetaDataType
> =
  // define the properties of the type
  {
    /**
     * role which the user has on the object, e.g. "ProjectCoordinator" on a project entity
     */
    '@type': RoleType
    /**
     * timestamp when the UserObjectRole has been created
     */
    createdAt?: string
    /**
     * creator of the UserObjectRole
     */
    createdBy?: IUser
    /**
     * Date when a given role expires. Null if the role does not expire.
     */
    expiresAt?: Date | string
    /**
     * meta information connected to the role a user has to an object, e.g. motivation + skills for a user-project-role
     */
    metadata?: MetaDataType
    /**
     * the associated object for which the user has a role, e.g. within the project the user is a project planner
     */
    object: ObjectType
    /**
     * user whose role is defined: User object or IRI, null if user is deleted
     */
    user: IUser
  } & INumericIdentifierModel extends infer AbstractUserObjectRole
  ? (
    // define which generic type parameter combinations are allowed
    [
      ExtendsWithAllPropertiesRequired<IChallenge, ObjectType> extends never ? false : "challenge",
      RoleType extends ChallengeRole ? "challengeRole" : false,
      MetaDataType extends StringBasedStringArray ? "metadata" : false
    ] extends ["challenge", "challengeRole", "metadata"]
    ? AbstractUserObjectRole
    : [
      ExtendsWithAllPropertiesRequired<IProject, ObjectType> extends never ? false : "project",
      RoleType extends MembershipRole ? "membership" : false,
      MetaDataType extends UserProjectMetaDataType ? "projectMetadata" : false
    ] extends ["project", "membership", "projectMetadata"]
    ? AbstractUserObjectRole
    : [
      ExtendsWithAllPropertiesRequired<IProgram, ObjectType> extends never ? false : "program",
      RoleType extends ProgramRole ? "programRole" : false,
      MetaDataType extends StringBasedStringArray ? "metadata" : false
    ] extends ["program", "programRole", "metadata"]
    ? AbstractUserObjectRole
    : [
      ExtendsWithAllPropertiesRequired<ITenant, ObjectType> extends never ? false : "provider",
      RoleType extends TenantRole ? "tenantRole" : false,
      MetaDataType extends StringBasedStringArray ? "metadata" : false
    ] extends ["provider", "tenantRole", "metadata"]
    ? AbstractUserObjectRole
    : never // no other type parameter combination is allowed
  )
  : never // no other object definition than AbstractUserObjectRole is allowed

/**
 * Union type of valid management UserObjectRoles.
 */
export type IUserManagementRole = IUserTenantRole | IUserProgramRole | IUserChallengeRole

/**
 * Union type of valid UserObjectRoles.
 */
export type IUserObjectRole = IUserManagementRole | IUserProjectRole

/**
 * List of those EntityTypes, that are used by UserObjectRoles as assigned object.
 */
export const entityTypesOfUORObjects = [
  EntityType.Project,
  EntityType.Challenge,
  EntityType.Program,
  EntityType.Provider
] satisfies UORObjectType[]

/**
 * Union type of those EntityTypes, that are used in UserObjectRoles as assigned object
 */
export type UORObjectType = EntityType.Project | EntityType.Challenge | EntityType.Program | EntityType.Provider

/**
 * Derives the specific IUserObjectRole from UserObjectRoleObjectType,
 * e.g. DerivedIUserObjectRole<IProject> would be from type IUserProjectRole
 */
export type DerivedIUserObjectRole<ObjectType extends UserObjectRoleObjectType> =
  [
    ExtendsWithAllPropertiesRequired<ITenant, ObjectType> extends never ? "no" : IUserTenantRole,
    ExtendsWithAllPropertiesRequired<IProgram, ObjectType> extends never ? "no" : IUserProgramRole,
    ExtendsWithAllPropertiesRequired<IChallenge, ObjectType> extends never ? "no" : IUserChallengeRole,
    ExtendsWithAllPropertiesRequired<IProject, ObjectType> extends never ? "no" : IUserProjectRole
  ] extends infer IsSameTypeChecker
  ? (IsSameTypeChecker extends [IUserTenantRole, "no", "no", "no"]
    ? IUserTenantRole
    : IsSameTypeChecker extends ["no", IUserProgramRole, "no", "no"]
    ? IUserProgramRole
    : IsSameTypeChecker extends ["no", "no", IUserChallengeRole, "no"]
    ? IUserChallengeRole
    : IsSameTypeChecker extends ["no", "no", "no", IUserProjectRole]
    ? IUserProjectRole
    : never)
  : never

/**
 * Derives the specific IUserObjectRole from the given EntityType.
 */
export type IUORFromEntityType<AbstractEntityType extends EntityType> =
  [
    AbstractEntityType extends EntityType.Provider ? IUserTenantRole : "no",
    AbstractEntityType extends EntityType.Program ? IUserProgramRole : "no",
    AbstractEntityType extends EntityType.Challenge ? IUserChallengeRole : "no",
    AbstractEntityType extends EntityType.Project ? IUserProjectRole : "no"
  ] extends infer IsSameTypeChecker
  ? (IsSameTypeChecker extends [IUserTenantRole, "no", "no", "no"]
    ? IUserTenantRole
    : IsSameTypeChecker extends ["no", IUserProgramRole, "no", "no"]
    ? IUserProgramRole
    : IsSameTypeChecker extends ["no", "no", IUserChallengeRole, "no"]
    ? IUserChallengeRole
    : IsSameTypeChecker extends ["no", "no", "no", IUserProjectRole]
    ? IUserProjectRole
    : never)
  : never

/**
 * An IUserProjectRole has specific metaData containing skills and motivations.
 *
 * Notation is specified by the API.
 */
export enum UserProjectRoleMetaDataKey {
  Skills = "skills",
  Motivation = "motivation"
}

/**
 * Only skill and motivation are allowed in user project meta data.
 */
export type UserProjectMetaDataType = { [key in UserProjectRoleMetaDataKey]: string }

/**
 * Represents the role within a project.
 *
 * Following project properties are delivered:
 * "@id", "@type", "id",
 * "accepted", "name", "slug",
 * "state",
 * "visibility",
 * "locked"
 */
export type IUserProjectRole = ValidUserObjectRole<IProject, MembershipRole, UserProjectMetaDataType>

/**
 * An IUserTenantRole represents an Accountmanager or a TenantManager.
 */
export type IUserTenantRole = ValidUserObjectRole<ITenant | IProvider, TenantRole, StringBasedStringArray>

/**
 * An IUserProgramRole represents a CommunityManager.
 */
export type IUserProgramRole = ValidUserObjectRole<IProgram, ProgramRole, StringBasedStringArray>

/**
 * An IUserChallengeRole represents a ChallengeManager.
 */
export type IUserChallengeRole = ValidUserObjectRole<IChallenge | IFund, ChallengeRole, StringBasedStringArray>

/**
 * An IWorkPackage represents a work package of a project, than can be a connector to several tasks. Usually a
 * project consists of more than one work package and a work package consists of more than one task.
 */
export interface IWorkPackage {
  "@type": string
  description?: string
  /**
   * a uuid, @see addWorkPackage()
   * no IRI, because it is not a seperate Entity, but part of a JSON
   */
  id?: string
  mainResponsibility?: string
  name?: string
  /** for display (AP1, AP2, ...) and for sorting */
  order?: number
}



/* ************************************************************************** */
/* Enum(erations) to standardize the usage of frequently used constants       */
/* using no prefix                                                            */
/* ************************************************************************** */

/**
 * A CostSubCategory represents a subtype of costs for (currently) Materialcosts/Sachkosten, e.G. "rent".
 *
 * Notation is specified by the API.
 * To add new entries or change or delete entries, it is important to adapt the places in the
 * PROJEKTFABRIK-application, where they are used.
 *
 * @see: /doc/index.md, Abschnitt "ResourceRequirements und ihre Enum(erationen)"
 */
export enum CostSubCategory {
  //  Administrative = "administrative",
  Honorarium = "honorarium",
  Rent = "rent",
  Subsistence = "subsistence",
  Traveling = "traveling",
  Other = "other"
  //  Fees = "fees",
  //  Custom = "custom", // for custom source types
}

/**
 * Types of Downloads
 *
 * NOTE: do not change without changing it it the backend, e.g. links in emails/pdf
 *
 * @todo: is this the right place for this enum?
 */
export enum DownloadType {
  ProposalAttachment = "proposal-attachment",
  ProposalPdf = "proposal-pdf",
  ProposalTeamPdf = "proposal-team-pdf",
  ProjectPdf = "project-pdf",
  TeamUpload = "team-upload",
}

/**
 * Contains all possible Sustainable Development Goals (SDGs), defined by the United Nations.
 */
export const SDGList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17] as const

/**
 * The 17 Sustainable Development Goals (SDGs) are defined by the United Nations.
 * Therefor a SDG is a number from 1 to 17.
 * @see https://www.plan.de/sdg-nachhaltige-entwicklungsziele.html?sc=IDQ24100
 */
export type SDG = typeof SDGList[number]

export enum FeedbackInvitationState {
  Active = 'active',
  Expired = 'expired',
  Preparing = 'preparation',
  All = 'all',
}

/**
 * Receiver for feeback invitations may be the connected users of a entity, so its IRI is
 * given as receiver, or it may be a different set of receivers, especially: 'all' users
 */
export enum FeedbackInvitationReceiver {
  /** all users as receivers. The string 'all' is given by the backend. */
  All = 'all'
}

/**
 * A FeedbackPostType frames possible types of posts, a user may contribute to an discussion by his post
 *
 * Notation is specified by the API.
 */
export enum FeedbackPostType {
  Hint = 'hint',
  Criticism = 'criticism',
  Troll = 'troll',
  Question = 'question',
  Support = 'support'
}

/**
 * FeedbackRoles frames possible roles, that may be allowed to give feedback
 *
 * Notation is specified by the API.
 */
export enum FeedbackRoles {
  Coordinator = 'coordinator',
  Planner = 'planner',
  Observer = 'observer',
  CommunityManager = 'communityManager',
  RegisteredUser = 'registeredUser',
}

/**
 * The ProgramType frames possible program types, that the provider determines for his
 * programs.
 */
export enum ProgramType {
  CitizenParticipation = "citizenParticipation",
  Custom = "custom",
  FundManagement = "fundManagement"
}

export enum ProjectVisibility {
  /**
   * Everybody can see everything from that project, as if they were an
   * observer. Only limited by a program's restricted user group if applicable.
   * Also, the project members are not visible (again as it is for observers).
   */
  Transparent = 'transparent',
  /**
   * Default: Profile data visible to anybody (respecting the program's
   * restricted user group), private data only to members.
   */
  Visible = 'visible',
  /**
   * Same as "visible", with the only difference being that limited projects
   * are *not* returned in the program's project collection. Direct access
   * via ID/slug is still possible. The collection can still be filtered for
   * limited projects by related managers.
   */
  Limited = 'limited',
  /**
   * Hidden projects are not returned in a program's project collection and
   * cannot be accessed via ID/slug, except by project members. The collection
   * can still be filtered for hidden projects by related managers.
   */
  Hidden = 'hidden'
}

/**
 * Defines all available types of a tenant.
 * Notation is specified by the API.
 *
 * Order is important and determines the order of entries in the UI.
 */
export enum TenantNature {
  Municipality = "municipality",
  SocialOrganization = "socialOrganization",
  Company = "company",
  Custom = "custom",
}

/**
 * Defines all available transitions for ITenant.
 * Notation is specified by the API.
 */
export enum TenantTransition {
  Activate = "activate",
  End = "end"
}

// #region roles
/**
 * A MembershipRole frames the possible roles for a project team member.
 *
 * Notation is specified by the API.
 */
export enum MembershipRole {
  Coordinator = "ProjectCoordinator",
  Observer = "ProjectObserver",
  Planner = "ProjectPlanner",

  /**
   * @deprecated delete as soon as Onboarding is migrated.
   * // TODO onboarding
   */
  Applicant = "Applicant"
}

/**
 * An user can be an AccountManager or a TenantManager of a tenant.
 *
 * Notation is specified by the API.
 */
export enum TenantRole {
  Accountmanager = 'AccountManager',
  TenantManager = 'TenantManager'
}

/**
 * An user can be the CommunityManager of a program and its projects.
 *
 * Notation is specified by the API.
 */
export enum ProgramRole {
  CommunityManager = 'CommunityManager'
}

/**
 * An user can be the ChallengeManager of a challenge and its proposals or of a fund and it's applications.
 *
 * Notation is specified by the API.
 */
export enum ChallengeRole {
  ChallengeManager = 'ChallengeManager'
}

/** conclusion of all platform wide existing user role types */
export type UserObjectRoleType = ChallengeRole | ProgramRole | TenantRole | MembershipRole

/** summary of all object type for which UserObjectRoles exists */
export type UserObjectRoleObjectType = IChallenge | IProgram | ITenant | IProject

// #endregion roles

/**
 * A ObligationState frames, how a data must be handled: the relevant data is: NotWanted, Required or Optional
 * Currently (07.08.2022) only NotWanted and Required are used in ProposalContent-Definition.
 *
 * Notation is specified by the API.
 */
export enum ObligationState {
  NotWanted = "",
  Optional = "optional",
  Required = "required",
}

/**
 * An PartnershipState frames the options for the status of a partnership
 *
 * Notation is specified by the API.
 */
export enum PartnershipState {
  Accepted = "accepted",
  Open = "open",
  Requested = "requested",
}

/**
 * The ImplementationState frames the state of a project regarding its implementation.
 *
 * These values are allowed when the coordinator changes the
 * implementationState, no further rules are applied on the backend.
 *
 * Notation is specified by the API.
 */
export enum ImplementationState {
  /** project is still in earliest self-discovery/self-description phase */
  Profiling = "profiling", // neuer Status => früher: ProjectProgress.CreatingProfile
  /** project is planning */
  Planning = "planning", // neuer Status => früher: ProjectProgress.CreatingPlan
  /** project is looking for members/resourcers/materials ... */
  Sourcing = "sourcing", // neuer Status
  /** project is in implementing phase */
  Implementing = "implementing", // neuer Status
  /** project is finished */
  Finished = "finished",
}

/**
 * Contains all ImplementationStates in a beginning to ending order.
 */
export const implementationStateOrder: ImplementationState[] = [
  ImplementationState.Profiling,
  ImplementationState.Planning,
  ImplementationState.Implementing,
  ImplementationState.Sourcing,
  ImplementationState.Finished
]

/**
 * The ProjectProgress frames possible states for a project progress: how far it has evolved.
 *
 * Notation is specified by the API.
 * @deprecated replaced by adapted ImplementationState
 * @todo multi delete after onboarding, marketplace and management are is refactored
 */
export enum ProjectProgress {
  Idea = "idea",
  CreatingProfile = "creating_profile",
  CreatingPlan = "creating_plan",
  CreatingProposal = "creating_proposal",
  SubmittingProposal = "submitting_proposal",
  ProposalSubmitted = "proposal_submitted",
}

/**
 * The ProjectProgress frames possible states of a project, especially if it is (still) active.
 *
 * Notation is specified by the API.
 */
export enum ProjectState {
  Active = "active",
  Inactive = "inactive",
  Deactivated = "deactivated",
}

/**
 * The ProgramState frames possible states of a program.
 *
 * Notation is specified by the API.
 */
export enum ProgramState {
  Active = "active",
  Created = "created",
  Ended = "ended"
}

/**
 * Contains all existing states of programs as array
 * to be used in code and tests to ensure complete list with same order.
 */
export const allProgramStates: ProgramState[] = Object.values(ProgramState)

/**
 * Defines all available transitions for IProgram.
 * Notation is specified by the API.
 */
export enum ProgramTransition {
  Activate = "activate",
  End = "end"
}


export enum RatePlanState {
  Active = "active",
  Created = "created",
  Feprecated = "deprecated"
}

/**
 * A RelevanceEstimation frames the intensity of the impact. Is is determined by the user.
 */
export enum RelevanceEstimation {
  Unvalued = 0,
  VeryLow = 10,
  Low = 25,
  Middle = 50,
  High = 75,
  VeryHigh = 100,
}

/**
 * A ResourceFinancialType frames possible types of costs, to form a consistent resource plan.
 *
 * Notation is specified by the API.
 * To add new entries or change or delete entries, it is important to adapt the places in the
 * PROJEKTFABRIK-application, where they are used.
 *
 * @see: /doc/index.md, Abschnitt "ResourceRequirements und ihre Enum(erationen)"
 */
export enum ResourceCostCategory {
  Investment = "investment",
  Material = "material",
  Personnel = "personnel",
  //  Proceeds = "proceeds",
  Other = "other",
  UnSet = ""
}


/**
 * A ResourceSource frames possible sources for ResourceRequirements, depending on the chosen SourceType
 *
 * Notation is specified by the API.
 * To add new entries or change or delete entries, it is important to adapt the places in the
 * PROJEKTFABRIK-application, where they are used.
 *
 * @see: /doc/index.md, Abschnitt "ResourceRequirements und ihre Enum(erationen)"
 */
export enum ResourceSource {
  Custom = 'custom',
  Fund = 'fund', // when sourceType === funding
  //  OwnContribution = 'ownContribution', // when sourceType === OwnResources
  OwnFunds = 'ownFunds', // when sourceType === OwnResources
  OwnMaterialContribution = "ownMaterialContribution", // when sourceType === OwnResources
  OwnEffortContribution = 'ownEffortContribution',
  Partner = 'partner', // when sourceType === ThirdParty
  NoSource = ''
}

/**
 * A ResourceUnit frames the options of units, a resource can be measured in.
 *
 * To add new entries or change or delete entries, it is important to adapt the places in the
 * PROJEKTFABRIK-application, where they are used.
 *
 * Notation is specified by the API.
 *
 * @see: /doc/index.md, Abschnitt "ResourceRequirements und ihre Enum(erationen)"
 */
export enum ResourceUnit {
  Custom = "custom",
  Flat = "flat",
  Pieces = "pieces",
  Hours = "hours",
  Months = "months",
  Years = "years",
  Kilometers = "kilometers",
  OvernightStays = "overnightStays"
}


/**
 * A ResourceSourceType represents a possible source for a needed resource or the needed amount of money.
 *
 * To add new entries or change or delete entries, it is important to adapt the places in the
 * PROJEKTFABRIK-application, where they are used.
 *
 * @see: /doc/index.md, Abschnitt "ResourceRequirements und ihre Enum(erationen)"
 */
export enum ResourceSourceType {
  Funding = "funding",
  OwnResources = "ownResources",
  Proceeds = "proceeds",
  ThirdParty = "thirdParty", // for custom source types
}


/**
 * A SelfAssessment frames the options of how far a project or a part of it has evolved. It is used by the users themself,
 * thats why they are called SelfAssessments.
 */
export enum SelfAssessment {
  Starting = 0,
  MakingProgress = 25,
  HalfFinished = 50,
  AlmostFinished = 75,
  Complete = 100,
}

/**
 * This selfAssessment steps are used for the project goals.
 * This steps are defined by the client.
 */
export enum GoalSelfAssessment {
  ToBeDetermined = 0,
  Started = 1,
  DoneWell = 25,
  HalfOnTheWay = 50,
  SeeingTheFinish = 75,
  TargetArchieved = 100,
}

/**
 * A SupportRequest has to have one support role. What is the project looking for?
 */
export enum SupporterRole {
  /** Sponsor/Geldgeber */
  Sponsor = 'sponsor',
  /** Dienstleister */
  Supplier = 'supplier',
  /** Feedbackgeber & Austauschpartner */
  FeedbackProvider = 'feedback_provider',
  /** Experten & Wissensträger */
  Expert = 'expert',
  /** Zugangschaffer & Botschafter */
  Ambassador = 'ambassador',
  /** Mitwirkende: Teammitglieder & Mitstreiter */
  Contributor = 'contributor',
}

export enum SupportRequestState {
  Hidden = 'hidden',
  Public = 'public',
  Closed = 'closed'
}

/**
 * TimeDimension frames the options of a monthly or yearly view on time-relevant data, e.g. the ICashFlowEntry.
 *
 * Notation is specified by the API.
 * To add new entries or change or delete entries, it is important to adapt the places in the
 * PROJEKTFABRIK-application, where they are used.
 *
 * @see: /doc/index.md, Abschnitt "ResourceRequirements und ihre Enum(erationen)"
 */
export enum TimeDimension {
  Monthly = "monthly",
  UnSet = "",
  Yearly = "yearly"
}

/**
 * The TenantState frames possible states of a tenant.
 *
 * Notation is specified by the API.
 */
export enum TenantState {
  Active = "active",
  Requested = "requested",
  Ended = "ended"
}

/**
 * Contains all existing states of tenants as array
 * to be used in code and tests to ensure complete list with same order.
 */
export const allTenantStates: TenantState[] = Object.values(TenantState)

/**
 * An UserRole frames the options for Roles, that exists in the system.
 *
 * Notation is specified by the API.
 *
 * @todo multi refactor ProcessManager to PlatformManager and delete Admin
 * @see https://futureprojects.atlassian.net/browse/FCP-1441
 */
export enum UserRole {
  User = "ROLE_USER",
  PlatformManager = "ROLE_PLATFORM_MANAGER",
  // @todo should not be an allowed role for any actions, added here for authenticated links
  Guest = "ROLE_GUEST",

  // @todo here for loading users and determining if the entity was loaded when the currentuser
  // was logged in or in the state from registration etc.
  Self = "self",

  // @todo multi following user roles have to be removed and permissions have to adapted, checked by UserObjectRoles
  // https://futureprojects.atlassian.net/browse/FCP-1440
  /** @deprecated */
  ProcessManager = "ROLE_PROCESS_MANAGER",
}

/**
 * An Userprivilege determines how much a user can see of a certain entity
 *
 * Notation is specified by the API.
 */
export enum UserPrivileges {
  // only forFund, Project, Program, Tenant
  Read = "read",
  // only forFund, Project, Program, Tenant
  ReadPrivate = "readPrivate",
  // only for FundApplication, Proposals
  ReadAdministrative = "readAdministrative",
}

/**
 * Is used to determine in an email, which user will receive the email.
 * Currently, simply to decide, if all users or a selection of users will receive the email.
 *
 * Notation is specified by the API.
 */
export enum UserEmailReceiverOption {
  All = "ALL_USERS",
  Selection = "SELECTED_USERS"
  // @todo Question add userRole as Filter?
}